import { useState, useCallback, useRef, useEffect } from 'react'
import type { StudentRequest, GroupOption, GradeOption } from 'api/arcMosaico/types'
import {
  getStudentRequests,
  acceptStudentRequest,
  declineStudentRequest,
  getGroupOptions,
  getGradeOptions,
  createGroupQuick,
  isUnauthorizedError,
} from '../../service/studentRequestsService'

export interface RowState {
  groupIds: string[]
  gradeId: string | null
  error: { group?: boolean; grade?: boolean }
}

const EMPTY_ROW: RowState = { groupIds: [], gradeId: null, error: {} }

export function useStudentRequests() {
  const [requests, setRequests] = useState<StudentRequest[]>([])
  const [groups, setGroups] = useState<GroupOption[]>([])
  const [grades, setGrades] = useState<GradeOption[]>([])
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({})
  const [loading, setLoading] = useState(false)
  const [pendingCount, setPendingCount] = useState(0)
  const [error, setError] = useState<string | null>(null)
  // Idle session expiry detected by the background count polling: the widget
  // stops the interval — no toast, no redirect (the user did nothing)
  const [sessionExpired, setSessionExpired] = useState(false)
  // courseId from existing groups — needed for the create-group backend endpoint
  const courseIdRef = useRef<string | null>(null)

  // Track per-row loading to prevent double-clicks and show UI feedback
  const [rowLoading, setRowLoading] = useState<Record<string, 'accepting' | 'declining'>>({})
  // Track whether a bulk accept-all operation is running
  const [acceptingAll, setAcceptingAll] = useState(false)

  // Ref to access latest rowStates in callbacks without stale closures
  const rowStatesRef = useRef(rowStates)
  rowStatesRef.current = rowStates

  // Ref to track latest requests for accept-all loop (avoids stale closure)
  const requestsRef = useRef(requests)
  requestsRef.current = requests

  // Abort controller for cancelling in-flight fetches on unmount or re-fetch
  const fetchAbortRef = useRef<AbortController | null>(null)
  const countAbortRef = useRef<AbortController | null>(null)
  // Track mounted state to prevent setState after unmount
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      // Cancel any in-flight requests on unmount
      fetchAbortRef.current?.abort()
      countAbortRef.current?.abort()
    }
  }, [])

  const fetchData = useCallback(async () => {
    // Cancel any previous in-flight fetch to avoid race conditions (e.g. rapid open/close)
    fetchAbortRef.current?.abort()
    const abortController = new AbortController()
    fetchAbortRef.current = abortController

    setLoading(true)
    setError(null)
    try {
      const [reqData, groupResult, gradeData] = await Promise.all([
        getStudentRequests(),
        getGroupOptions(),
        getGradeOptions(),
      ])

      // If this fetch was aborted (superseded by a newer one), discard results
      if (abortController.signal.aborted) return

      if (!mountedRef.current) return

      setRequests(reqData.students)
      setPendingCount(reqData.pagination.count)
      setGroups(groupResult.groups)
      courseIdRef.current = groupResult.courseId
      setGrades(gradeData)

      const states: Record<string, RowState> = {}
      reqData.students.forEach(s => {
        states[s.id] = { ...EMPTY_ROW }
      })
      setRowStates(states)
      setRowLoading({})
    } catch (err) {
      // Aborted fetches are expected (user re-opened drawer); do not surface them
      if (abortController.signal.aborted) return
      if (!mountedRef.current) return

      const message = err instanceof Error ? err.message : 'Failed to load data'
      setError(message)
    } finally {
      if (!abortController.signal.aborted && mountedRef.current) {
        setLoading(false)
      }
    }
  }, [])

  const refreshCount = useCallback(async () => {
    // Cancel any previous count refresh
    countAbortRef.current?.abort()
    const abortController = new AbortController()
    countAbortRef.current = abortController

    try {
      // Silent variant: a background 401 must not redirect/toast (idle expiry)
      const data = await getStudentRequests(1, 1, { silentUnauthorized: true })
      if (abortController.signal.aborted || !mountedRef.current) return
      setPendingCount(data.pagination.count)
    } catch (err) {
      if (abortController.signal.aborted || !mountedRef.current) return
      if (isUnauthorizedError(err)) {
        setSessionExpired(true)
        console.warn('student requests polling stopped: session expired')
        return
      }
      // Background count refresh failure: log but don't block UI.
      // The badge will show the stale count, which is acceptable for a non-critical indicator.
      console.warn('[StudentRequests] Failed to refresh pending count:', err instanceof Error ? err.message : err)
    }
  }, [])

  const updateRowGroups = useCallback((requestId: string, groupIds: string[]) => {
    setRowStates(prev => {
      const existing = prev[requestId]
      if (!existing) return prev
      return {
        ...prev,
        [requestId]: { ...existing, groupIds, error: { ...existing.error, group: false } },
      }
    })
  }, [])

  const updateRowGrade = useCallback((requestId: string, gradeId: string | null) => {
    setRowStates(prev => {
      const existing = prev[requestId]
      if (!existing) return prev
      return {
        ...prev,
        [requestId]: { ...existing, gradeId, error: { ...existing.error, grade: false } },
      }
    })
  }, [])

  const removeRequest = useCallback((requestId: string) => {
    setRequests(prev => prev.filter(r => r.id !== requestId))
    setPendingCount(prev => Math.max(0, prev - 1))
    setRowStates(prev => {
      const next = { ...prev }
      delete next[requestId]
      return next
    })
    setRowLoading(prev => {
      const next = { ...prev }
      delete next[requestId]
      return next
    })
  }, [])

  const handleAccept = useCallback(async (requestId: string): Promise<boolean> => {
    const row = rowStatesRef.current[requestId]
    if (!row) {
      console.warn(`[StudentRequests] handleAccept: no row state for request ${requestId}`)
      return false
    }

    const hasGroup = row.groupIds.length > 0
    const hasGrade = !!row.gradeId
    if (!hasGroup || !hasGrade) {
      setRowStates(prev => ({
        ...prev,
        [requestId]: { ...prev[requestId], error: { group: !hasGroup, grade: !hasGrade } },
      }))
      return false
    }

    // Prevent double-click: check if row is already being processed
    setRowLoading(prev => {
      if (prev[requestId]) return prev
      return { ...prev, [requestId]: 'accepting' }
    })

    try {
      await acceptStudentRequest(requestId, {
        groupSchoolTermIds: row.groupIds,
        gradeId: row.gradeId!,
      })
      if (mountedRef.current) {
        removeRequest(requestId)
      }
      return true
    } catch (err) {
      if (mountedRef.current) {
        setRowLoading(prev => {
          const next = { ...prev }
          delete next[requestId]
          return next
        })
      }
      // Re-throw so the caller (modal) can show a user-facing notification
      throw err
    }
  }, [removeRequest])

  const handleDecline = useCallback(async (requestId: string) => {
    // Prevent double-click
    setRowLoading(prev => {
      if (prev[requestId]) return prev
      return { ...prev, [requestId]: 'declining' }
    })

    try {
      await declineStudentRequest(requestId)
      if (mountedRef.current) {
        removeRequest(requestId)
      }
    } catch (err) {
      if (mountedRef.current) {
        setRowLoading(prev => {
          const next = { ...prev }
          delete next[requestId]
          return next
        })
      }
      // Re-throw so the caller (modal) can show a user-facing notification
      throw err
    }
  }, [removeRequest])

  const validateAll = useCallback((): boolean => {
    let valid = true
    setRowStates(prev => {
      const next = { ...prev }
      Object.entries(next).forEach(([id, row]) => {
        const hasGroup = row.groupIds.length > 0
        const hasGrade = !!row.gradeId
        if (!hasGroup || !hasGrade) {
          valid = false
          next[id] = { ...row, error: { group: !hasGroup, grade: !hasGrade } }
        }
      })
      return next
    })
    return valid
  }, [])

  const handleAcceptAll = useCallback(async (): Promise<{ accepted: number; failed: number }> => {
    setAcceptingAll(true)
    let accepted = 0
    let failed = 0

    try {
      // Snapshot IDs at the start to avoid stale closure issues when removeRequest
      // mutates the requests array during iteration
      const idsToAccept = requestsRef.current.map(r => r.id)

      for (const id of idsToAccept) {
        // Check if row still exists (could have been removed by another tab or concurrent op)
        const row = rowStatesRef.current[id]
        if (!row) continue

        try {
          const success = await handleAccept(id)
          if (success) {
            accepted++
          } else {
            // Validation failed (missing group/grade) -- counted as failure
            failed++
          }
        } catch {
          failed++
          // Continue processing remaining requests even if one fails.
          // The user sees the failed rows still in the list.
        }
      }

      return { accepted, failed }
    } finally {
      if (mountedRef.current) {
        setAcceptingAll(false)
      }
    }
  }, [handleAccept])

  const fillDown = useCallback((field: 'group' | 'grade') => {
    setRowStates(prev => {
      const ids = Object.keys(prev)
      if (ids.length < 2) return prev

      const firstRow = prev[ids[0]]
      if (!firstRow) return prev

      // Only fill if the first row actually has a value for the field
      if (field === 'group' && firstRow.groupIds.length === 0) return prev
      if (field === 'grade' && !firstRow.gradeId) return prev

      const next = { ...prev }
      ids.forEach(id => {
        const row = next[id]
        if (field === 'group' && row.groupIds.length === 0) {
          next[id] = { ...row, groupIds: [...firstRow.groupIds], error: { ...row.error, group: false } }
        }
        if (field === 'grade' && !row.gradeId) {
          next[id] = { ...row, gradeId: firstRow.gradeId, error: { ...row.error, grade: false } }
        }
      })
      return next
    })
  }, [])

  const addGroup = useCallback(async (name: string): Promise<GroupOption> => {
    if (!courseIdRef.current) {
      throw new Error('Cannot create group: no course available')
    }
    const newGroup = await (createGroupQuick as (name: string, courseId?: string | null) => Promise<GroupOption>)(name, courseIdRef.current)
    if (mountedRef.current) {
      setGroups(prev => [...prev, newGroup])
    }
    return newGroup
  }, [])

  const firstRow = requests[0] ? rowStates[requests[0].id] : null
  const canFillDownGroup = !!firstRow && firstRow.groupIds.length > 0
  const canFillDownGrade = !!firstRow && !!firstRow.gradeId

  // Prevent individual actions while bulk accept is running
  const isRowBusy = useCallback((requestId: string): boolean => {
    return !!rowLoading[requestId] || acceptingAll
  }, [rowLoading, acceptingAll])

  return {
    requests,
    groups,
    grades,
    rowStates,
    rowLoading,
    loading,
    pendingCount,
    error,
    sessionExpired,
    acceptingAll,
    canFillDownGroup,
    canFillDownGrade,
    isRowBusy,
    fetchData,
    refreshCount,
    updateRowGroups,
    updateRowGrade,
    handleAccept,
    handleDecline,
    handleAcceptAll,
    validateAll,
    fillDown,
    addGroup,
  }
}
